import { useCallback, useState } from 'react';
import { useSurveyStore } from '@/stores/survey-store';
import { useSessionStore } from '@/stores/session-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useResponseStore } from '@/stores/response-store';
import { useUIStore } from '@/stores/ui-store';
import { ProgressBar } from '@/components/navigation/progress-bar';
import { PageNavigator } from '@/components/navigation/page-navigator';
import { QuestionPage } from './question-page';
import { LoadingOverlay } from '@/components/ui';

export function SurveyLayout() {
  const { theme, getQuestionsByPage, isRtl } =
    useSurveyStore();
  const { complete } = useSessionStore();
  const {
    currentPage,
    goToNextPage,
    goToPreviousPage,
    hiddenQuestions,
  } = useNavigationStore();
  const { savePageAnswers, answers } = useResponseStore();
  const {
    setValidationError,
    clearAllValidationErrors,
  } = useUIStore();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get visible questions for current page
  const getVisiblePageQuestions = useCallback(() => {
    return getQuestionsByPage(currentPage).filter(
      (q) => !hiddenQuestions.has(q.code)
    );
  }, [currentPage, getQuestionsByPage, hiddenQuestions]);

  // Validate current page
  const validateCurrentPage = useCallback((): boolean => {
    const pageQuestions = getVisiblePageQuestions();
    let isValid = true;

    clearAllValidationErrors();

    for (const question of pageQuestions) {
      const value = answers[question.code];
      const errors: string[] = [];

      // Required validation
      if (question.required) {
        const isEmpty =
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(value).length === 0);

        if (isEmpty) {
          errors.push('This question is required');
        }
      }

      if (errors.length > 0) {
        setValidationError(question.code, errors);
        isValid = false;
      }
    }

    return isValid;
  }, [
    getVisiblePageQuestions,
    answers,
    clearAllValidationErrors,
    setValidationError,
  ]);

  // Handle next page
  const handleNext = useCallback(async () => {
    if (!validateCurrentPage()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Save page answers
      const pageQuestionCodes = getVisiblePageQuestions().map((q) => q.code);
      const terminal = await savePageAnswers(currentPage, pageQuestionCodes);

      // The BE ended the session (overquota/timeout/etc) — stop here; the
      // session store now holds the terminal and SurveyPage will redirect.
      if (terminal) {
        return;
      }

      // Go to next page
      goToNextPage();
    } catch (error) {
      console.error('Failed to save answers:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateCurrentPage,
    getVisiblePageQuestions,
    savePageAnswers,
    currentPage,
    goToNextPage,
  ]);

  // Handle back
  const handleBack = useCallback(() => {
    goToPreviousPage();
  }, [goToPreviousPage]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!validateCurrentPage()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Save final page answers
      const pageQuestionCodes = getVisiblePageQuestions().map((q) => q.code);
      const terminal = await savePageAnswers(currentPage, pageQuestionCodes);

      // A terminal on the final save means the BE already ended the session —
      // do NOT also call complete(); SurveyPage redirects from the terminal.
      if (terminal) {
        setIsSubmitting(false);
        return;
      }

      // Complete the session
      await complete();

      // Redirect will be handled by the SurveyPage component
    } catch (error) {
      console.error('Failed to submit survey:', error);
      setIsSubmitting(false);
    }
  }, [
    validateCurrentPage,
    getVisiblePageQuestions,
    savePageAnswers,
    currentPage,
    complete,
  ]);

  // Apply theme
  const containerStyle = theme.backgroundColor
    ? { backgroundColor: theme.backgroundColor }
    : {};

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={containerStyle}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {/* Header with logo and progress */}
      <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="survey-container py-4">
          {/* Logo */}
          {theme.logoUrl && (
            <div className="mb-4">
              <img
                src={theme.logoUrl}
                alt="Survey"
                className="h-8 w-auto"
              />
            </div>
          )}

          {/* Progress bar */}
          <ProgressBar />
        </div>
      </header>

      {/* Main content */}
      <main className="survey-container py-6">
        <QuestionPage />
      </main>

      {/* Navigation footer */}
      <footer className="sticky bottom-0 z-10 bg-white border-t border-gray-200">
        <div className="survey-container py-4">
          <PageNavigator
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      </footer>

      {/* Loading overlay */}
      {isSubmitting && <LoadingOverlay message="Saving..." />}
    </div>
  );
}
